"use client";

import { useEffect } from "react";
import { identifyUser, registerSuperProps, track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth/context";
import { useChores } from "@/lib/hooks/useChores";
import { useMembers } from "@/lib/hooks/useMembers";
import { useTodaysCompletions } from "@/lib/hooks/useTodaysCompletions";
import { useTodaysHouseholdCompletions } from "@/lib/hooks/useTodaysHouseholdCompletions";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useTransactions } from "@/lib/hooks/useTransactions";
import { useChoreProposals } from "@/lib/hooks/useChoreProposals";
import { useInstantSubmissions } from "@/lib/hooks/useInstantSubmissions";
import { useGoals } from "@/lib/hooks/useGoals";

/**
 * Renders nothing — but mounts every realtime hook so subscriptions start
 * the moment user enters the (app) shell. Each hook caches its data in a
 * module-level Map. Subsequent screen mounts read from cache instantly,
 * eliminating the "Ładowanie…" flash on first tab switch.
 *
 * Subscriptions stay alive as long as the AppShell is mounted (i.e. user
 * is in any /(app)/* route). Logout / unmount cleans them up.
 */

// session_started raz na page load (nie na remount AppShell).
let sessionTracked = false;
export function DataPrefetcher() {
  const { userDoc, firebaseUser } = useAuth();
  const hid = userDoc?.householdId;
  const uid = firebaseUser?.uid;

  // Wspólne — wszyscy widzą
  useChores(hid);
  const { members } = useMembers(hid);

  // Analityka sesji: pseudonimowa identyfikacja po uid + session_started
  // raz na page load. Skład rodziny jako super properties — pozwala
  // segmentować retencję po childrenCount/parentsCount bez joinów.
  useEffect(() => {
    if (!uid || !userDoc || sessionTracked) return;
    sessionTracked = true;
    identifyUser(uid, { role: userDoc.role });
    track("session_started", { role: userDoc.role });
  }, [uid, userDoc]);

  useEffect(() => {
    if (!uid || members.length === 0) return;
    registerSuperProps({
      role: userDoc?.role ?? "unknown",
      childrenCount: members.filter((m) => m.role === "child").length,
      parentsCount: members.filter((m) => m.role === "parent").length,
    });
  }, [uid, userDoc, members]);

  // Per-user
  useTodaysCompletions(hid, uid);
  useCompletions(hid, { status: "approved", completedBy: uid });
  useTransactions(hid, uid);
  useGoals(hid, uid);
  useChoreProposals(hid, { proposedBy: uid });

  // Globalne pending (parent będzie potrzebował, child może też widzieć)
  useCompletions(hid, { status: "pending" });
  useChoreProposals(hid, { status: "pending" });
  useInstantSubmissions(hid, { status: "pending" });

  // Wszystkie approved w HH — używane w Settings do pokazania Level/XP
  // każdego dziecka rodzeństwu i rodzicowi (transparentność rodzinna).
  // Jeden listener obsługuje wszystkich; filtracja per dziecko po stronie
  // klienta z `completedBy`.
  useCompletions(hid, { status: "approved" });

  // Dzisiejsze completion całej rodziny — parent widzi status zadań wszystkich
  // dzieci w /chores (filtr Wykonane, AI badge przy auto-approved).
  useTodaysHouseholdCompletions(hid);

  return null;
}
